import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, ClipboardCheck, Search, X } from "lucide-react";
import { isSupabaseConfigured } from "../lib/supabase";
import { loadValidationQueue, reviewTaskValidation, type ReviewDecision, type ValidationQueueItem } from "../services/validations";
import { MONTHS, type WorkspaceData } from "../types";
import "./ValidationPanel.css";

type Filter = "pending" | "observed" | "approved" | "all";

function demoQueue(): ValidationQueueItem[] {
  return [{
    validationId: "demo-validation-1", versionId: "demo-version-1", versionNumber: 1,
    submittedAt: new Date().toISOString(), centerCode: "03.01.01", centerName: "UDR CAJAMARCA",
    taskId: "demo-udr-task", taskCode: "M1.05.05.07", taskName: "Ejecutar acciones de Auditoría Electrónica de Prestaciones",
    activityCode: "M1.05.05", activityName: "Gestión de riesgos de prestaciones", unit: "Acción",
    values: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], gmrCode: "03.01",
    lineCode: "01.02", lineName: "GERENCIA DE RIESGO Y EVALUACIÓN DE LAS PRESTACIONES",
    justification: "Ajuste de la meta de la tarea para el periodo 2026",
    gmrStatus: "pending", lineStatus: "waiting", gmrComment: null, lineComment: null, reviewStage: "gmr",
  }];
}

function finalStatus(row: ValidationQueueItem) {
  if (row.gmrStatus === "observed" || row.lineStatus === "observed") return "observed";
  if (row.gmrStatus === "approved" && (row.lineStatus === "approved" || row.lineStatus === "not_required")) return "approved";
  return "pending";
}

export function ValidationPanel({ workspace, demoRows, onDemoRowsChange }: {
  workspace: WorkspaceData;
  demoRows?: ValidationQueueItem[];
  onDemoRowsChange?: (rows: ValidationQueueItem[]) => void;
}) {
  const [rows, setRows] = useState<ValidationQueueItem[]>(() => isSupabaseConfigured ? [] : demoQueue());
  const [filter, setFilter] = useState<Filter>("pending");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ValidationQueueItem | null>(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    loadValidationQueue(workspace.cycle.id)
      .then(setRows)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "No se pudo cargar la bandeja."))
      .finally(() => setLoading(false));
  }, [workspace.cycle.id]);

  const reviewerCode = workspace.costCenters.find((center) => center.id === workspace.selectedCenterId)?.code;
  const assignedRows = useMemo(() => {
    const source = !isSupabaseConfigured && demoRows ? demoRows : rows;
    if (isSupabaseConfigured || workspace.profile.role === "admin") return source;
    return source.filter((row) => row.gmrCode === reviewerCode || row.lineCode === reviewerCode);
  }, [demoRows, reviewerCode, rows, workspace.profile.role]);

  const reviewRows = useMemo(() => assignedRows.map((row) => {
    let reviewStage: ValidationQueueItem["reviewStage"] = null;
    if (row.gmrStatus === "pending" && (workspace.profile.role === "admin" || row.gmrCode === reviewerCode)) reviewStage = "gmr";
    else if (row.gmrStatus === "approved" && row.lineStatus === "pending" && (workspace.profile.role === "admin" || row.lineCode === reviewerCode)) reviewStage = "line";
    return { ...row, reviewStage };
  }), [assignedRows, reviewerCode, workspace.profile.role]);

  const counts = useMemo(() => ({
    pending: reviewRows.filter((row) => finalStatus(row) === "pending" && row.reviewStage).length,
    observed: reviewRows.filter((row) => finalStatus(row) === "observed").length,
    approved: reviewRows.filter((row) => finalStatus(row) === "approved").length,
  }), [reviewRows]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("es");
    return reviewRows.filter((row) => {
      const matchesFilter = filter === "all" || finalStatus(row) === filter;
      const matchesSearch = !query || `${row.centerCode} ${row.centerName} ${row.taskCode} ${row.taskName}`.toLocaleLowerCase("es").includes(query);
      return matchesFilter && matchesSearch;
    });
  }, [reviewRows, filter, search]);

  function openReview(row: ValidationQueueItem) {
    setSelected(row);
    setComment("");
    setError(null);
    setMessage(null);
  }

  async function decide(decision: ReviewDecision) {
    if (!selected?.reviewStage || (decision === "observed" && comment.trim().length < 5)) return;
    setSaving(true);
    setError(null);
    try {
      if (isSupabaseConfigured) await reviewTaskValidation(selected.validationId, selected.reviewStage, decision, comment);
      const source = !isSupabaseConfigured && demoRows ? demoRows : rows;
      const nextRows = source.map((row) => {
        if (row.validationId !== selected.validationId) return row;
        if (selected.reviewStage === "gmr") return {
          ...row, gmrStatus: decision, gmrComment: comment.trim() || null,
          lineStatus: decision === "approved" ? (row.lineCode ? "pending" : "not_required") : row.lineStatus,
          reviewStage: null,
        };
        return { ...row, lineStatus: decision, lineComment: comment.trim() || null, reviewStage: null };
      });
      if (!isSupabaseConfigured && onDemoRowsChange) onDemoRowsChange(nextRows);
      else setRows(nextRows);
      setMessage(decision === "approved" ? "La tarea fue aprobada." : "La tarea fue observada y devuelta a la UDR.");
      setSelected(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo registrar la validación.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page validation-page">
      <header className="page-header">
        <div><span className="eyebrow">CONTROL PREVIO</span><h1>Validación de metas UDR</h1><p>Revisa cada tarea asignada a tu GMR u órgano de línea.</p></div>
        <span className="admin-badge"><ClipboardCheck />Bandeja de validación</span>
      </header>

      {message && <div className="admin-message"><Check size={16} />{message}</div>}
      {error && !selected && <div className="validation-error"><AlertTriangle />{error}</div>}

      <section className="validation-summary">
        <button className={filter === "pending" ? "active" : ""} onClick={() => setFilter("pending")}><strong>{counts.pending}</strong><span>Pendientes</span></button>
        <button className={filter === "observed" ? "active" : ""} onClick={() => setFilter("observed")}><strong>{counts.observed}</strong><span>Observadas</span></button>
        <button className={filter === "approved" ? "active" : ""} onClick={() => setFilter("approved")}><strong>{counts.approved}</strong><span>Aprobadas</span></button>
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}><strong>{reviewRows.length}</strong><span>Todas</span></button>
      </section>

      <section className="admin-card validation-card">
        <div className="validation-toolbar"><div><h2>Tareas recibidas</h2><p>La aprobación se registra individualmente y queda en auditoría.</p></div><div className="search-box"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar UDR o tarea…" /></div></div>
        <div className="validation-table-wrap"><table className="validation-table">
          <thead><tr><th>UDR / versión</th><th>Tarea</th><th>Ruta</th><th>Estado</th><th>Acción</th></tr></thead>
          <tbody>{visibleRows.map((row) => {
            const status = finalStatus(row);
            return <tr key={row.validationId}>
              <td><strong>{row.centerCode} · {row.centerName}</strong><span>Propuesta enviada</span></td>
              <td><strong>{row.taskCode} · {row.taskName}</strong><span>{row.activityCode} · {row.unit}</span></td>
              <td><span>GMR {row.gmrCode}</span><small>{row.lineCode ? `Luego ${row.lineCode}` : "Validación final en GMR"}</small></td>
              <td><span className={`review-status ${status}`}>{status === "pending" ? row.gmrStatus === "pending" ? "Pendiente GMR" : "Pendiente órgano de línea" : status === "observed" ? "Observada" : "Aprobada"}</span></td>
              <td>{row.reviewStage ? <button className="review-button" onClick={() => openReview(row)}><ClipboardCheck />Revisar</button> : <span className="no-action">—</span>}</td>
            </tr>;
          })}</tbody>
        </table>{loading && <div className="table-loading">Cargando validaciones…</div>}{!loading && !visibleRows.length && <div className="table-loading">No hay tareas en esta bandeja.</div>}</div>
      </section>

      {selected && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => !saving && setSelected(null)}><div className="reopen-modal validation-modal" role="dialog" aria-modal="true" aria-labelledby="validation-title" onMouseDown={(event) => event.stopPropagation()}>
          <div className="modal-header"><div className="modal-icon"><ClipboardCheck /></div><div><span className="eyebrow">{selected.reviewStage === "gmr" ? `REVISIÓN GMR ${selected.gmrCode}` : `REVISIÓN ${selected.lineCode}`}</span><h2 id="validation-title">Validar tarea</h2></div><button onClick={() => setSelected(null)} disabled={saving}><X /></button></div>
          <div className="selected-center"><span>UDR de origen · propuesta enviada</span><strong>{selected.centerCode} · {selected.centerName}</strong></div>
          <div className="validation-task-detail"><span>{selected.activityCode} · {selected.activityName}</span><strong>{selected.taskCode} · {selected.taskName}</strong><small>Unidad: {selected.unit}</small></div>
          <div className="validation-months">{MONTHS.map((month, index) => <div key={month}><span>{month}</span><strong>{selected.values[index] ?? 0}</strong></div>)}</div>
          <div className="validation-justification"><span>Sustento de la tarea</span><p>{selected.justification}</p></div>
          <label className="reason-field">Comentario<textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Obligatorio si observa la tarea…" rows={3} /><small>La observación será visible para la UDR y quedará registrada en auditoría.</small></label>
          {error && <div className="validation-error"><AlertTriangle />{error}</div>}
          <div className="modal-actions validation-actions"><button className="observe-button" onClick={() => void decide("observed")} disabled={saving || comment.trim().length < 5}><AlertTriangle />Observar tarea</button><button className="primary-button" onClick={() => void decide("approved")} disabled={saving}><CheckCircle2 />{saving ? "Guardando…" : "Aprobar tarea"}</button></div>
        </div></div>
      )}
    </div>
  );
}
